import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

/* ---------------------------------------------------------------
   Utilidades de animación: revelado por scroll + contador animado.
   Sin librerías externas (el proyecto no usa framer-motion).
--------------------------------------------------------------- */

function useInView(threshold = 0.18) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible];
}

function Reveal({ as: Tag = 'div', delay = 0, className = '', children, ...rest }) {
  const [ref, visible] = useInView();
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'is-visible' : ''} ${className}`.trim()}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

function Counter({ to, suffix = '', duration = 1400 }) {
  const [ref, visible] = useInView(0.6);
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!visible) return undefined;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * to));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, to, duration]);

  return (
    <strong ref={ref}>
      {value}
      {suffix}
    </strong>
  );
}

/* ---------------------------------------------------------------
   Contenido
--------------------------------------------------------------- */

const stats = [
  { value: 40, suffix: '%', label: 'menos tiempo administrativo por semana' },
  { value: 6, suffix: ' módulos de IA', label: 'ya activos y funcionando, no en roadmap' },
  { value: 24, suffix: '/7', label: 'seguimiento académico, sin depender de horarios' },
];

const trustLogos = ['Colegio Aurora', 'Instituto Norte', 'Academia Prisma', 'Grupo Educativo Sol'];

const pillars = [
  {
    title: 'La IA ya funciona, no es una promesa',
    text: 'Motor de riesgo académico, Copiloto de Rectoría, Observador automático y Tutor IA ya están activos y funcionando hoy — no en un roadmap a 18 meses.',
    icon: '✦',
  },
  {
    title: 'El único con inteligencia predictiva en Colombia',
    text: 'Mientras otras plataformas colombianas todavía muestran notas, Playfesor anticipa qué estudiante va a perder el año antes de que sea tarde.',
    icon: '◎',
  },
  {
    title: 'Construido para el estándar MEN',
    text: 'Escala de valoración, períodos, boletines y observador siguen la normativa colombiana desde el primer día, sin adaptaciones manuales.',
    icon: '✓',
  },
  {
    title: 'Ya funciona, con datos reales',
    text: 'Playfesor no es un prototipo ni una maqueta comercial: tiene instituciones operando, datos reales y una base técnica sólida para escalar.',
    icon: '⚡',
  },
];

const features = [
  { title: 'Copiloto de Rectoría', text: 'El rector le pregunta en lenguaje natural y la IA responde con los datos reales de su colegio, cada mañana.', icon: '◈', badge: 'IA' },
  { title: 'Motor de riesgo académico', text: 'Calcula cada noche qué estudiantes están en riesgo de perder el año, con score y factores explicados.', icon: '⚠', badge: 'IA' },
  { title: 'Observador automático', text: 'Genera la observación académica oficial en segundos; el docente solo revisa y firma.', icon: '▣', badge: 'IA' },
  { title: 'Tutor IA para estudiantes', text: 'Acompañamiento personalizado 24/7 fuera del aula, adaptado al nivel y al currículo del colegio.', icon: '◐', badge: 'IA' },
  { title: 'WhatsApp para padres', text: 'Notificaciones automáticas de notas, inasistencias y alertas directo al canal que sí revisan los acudientes.', icon: '⌘' },
  { title: 'Portal de padres de familia', text: 'Los acudientes ven promedio, asistencia y nivel de riesgo de sus hijos sin llamar al colegio.', icon: '⌂' },
  { title: 'Centro de métricas institucional', text: 'Salud académica, ranking de grupos y docentes, mapa de calor por materia, en un solo tablero.', icon: '▤' },
  { title: 'Gemelo digital del colegio', text: 'Vista viva del estado del colegio en tiempo real, con proyección de qué pasa si se interviene o no.', icon: '◌' },
  { title: 'Planes de mejoramiento con IA', text: 'Diagnóstico, actividades de refuerzo y cronograma generados automáticamente cuando un estudiante pierde.', icon: '⟲' },
  { title: 'Asistencia y calificación', text: 'Registro de asistencia y calificación automática con retroalimentación inmediata para cada actividad.', icon: '✓' },
  { title: 'Boletines oficiales', text: 'Exporta boletines listos en escala MEN sin armar una sola hoja de cálculo a mano.', icon: '⎘' },
  { title: 'Multi-colegio desde el origen', text: 'Arquitectura pensada para redes de colegios y secretarías de educación, no solo para una sede.', icon: '⬡' },
];

const comparisonRows = [
  { label: 'Escala de valoración MEN (Colombia)', playfesor: true, phidias: true, q10: true, classroom: false },
  { label: 'Actividades interactivas con calificación automática', playfesor: true, phidias: 'partial', q10: false, classroom: false },
  { label: 'Motor de riesgo académico predictivo (IA)', playfesor: true, phidias: false, q10: false, classroom: false },
  { label: 'Copiloto ejecutivo en lenguaje natural', playfesor: true, phidias: false, q10: false, classroom: false },
  { label: 'Observador académico generado por IA', playfesor: true, phidias: false, q10: false, classroom: false },
  { label: 'Tutor IA para estudiantes, 24/7', playfesor: true, phidias: false, q10: false, classroom: false },
  { label: 'WhatsApp automático para padres', playfesor: true, phidias: 'partial', q10: false, classroom: false },
  { label: 'Portal dedicado para padres de familia', playfesor: true, phidias: 'partial', q10: 'partial', classroom: false },
  { label: 'Centro de métricas institucional', playfesor: true, phidias: false, q10: 'partial', classroom: false },
  { label: 'Onboarding sin curva de aprendizaje larga', playfesor: true, phidias: 'partial', q10: false, classroom: true },
];

const roles = [
  {
    role: 'Rector',
    problem: 'Se entera de los problemas cuando ya explotaron.',
    solution: 'Copiloto de Rectoría + informes automáticos + alertas ejecutivas.',
    impact: 'Pasa de reaccionar tarde a anticipar con datos, cada mañana.',
  },
  {
    role: 'Docente',
    problem: 'Escribir observaciones y detectar riesgo a tiempo consume horas.',
    solution: 'Observador automático + alertas de riesgo + recomendaciones pedagógicas.',
    impact: 'Lo que tomaba horas de redacción se revisa y firma en minutos.',
  },
  {
    role: 'Estudiante',
    problem: 'No tiene guía fuera del aula ni sabe en qué está fallando.',
    solution: 'Tutor IA disponible 24/7, adaptado a su nivel y a sus debilidades reales.',
    impact: 'Refuerzo personalizado sin depender de horarios ni de tutorías privadas.',
  },
  {
    role: 'Padre de familia',
    problem: 'No sabe cómo va su hijo hasta que llega el boletín.',
    solution: 'Portal de padres + notificaciones automáticas por WhatsApp.',
    impact: 'Se entera a tiempo y se convierte en aliado del proceso, no en espectador.',
  },
];

const plans = [
  {
    name: 'Aula Digital',
    tag: 'Básico',
    audience: 'Colegios pequeños y microcolegios',
    features: [
      'Gestión completa: colegios, grupos, docentes, estudiantes',
      'Motor de actividades interactivas con calificación automática',
      'Escala de valoración MEN',
      'Reporte por grupo y materia + export a Excel',
    ],
  },
  {
    name: 'Institución Inteligente',
    tag: 'Profesional',
    highlight: true,
    audience: 'Colegios medianos que ya buscan datos, no solo registro',
    features: [
      'Todo lo del plan Básico',
      'Dashboard director con semáforo por grupo',
      'Motor de riesgo académico y alertas automáticas',
      'Centro de métricas institucional',
      'Observador académico asistido por IA',
      'Módulo de asistencia y boletín oficial',
    ],
  },
  {
    name: 'Plataforma Educativa Completa',
    tag: 'Premium',
    audience: 'Colegios grandes y redes que quieren la experiencia completa',
    features: [
      'Todo lo del plan Profesional',
      'Copiloto de Rectoría con lenguaje natural',
      'Tutor IA personalizado para estudiantes',
      'WhatsApp y portal completo para padres',
      'Gemelo digital del colegio en tiempo real',
      'Soporte dedicado con acompañamiento directo',
    ],
  },
];

const steps = [
  'Agenda una demo y cuéntanos las metas de tu institución.',
  'Creamos tu ambiente institucional en Playfesor.',
  'Capacitamos a docentes, coordinadores y directivos.',
  'Tu equipo empieza a trabajar con datos, no con intuición.',
];

const testimonials = [
  {
    quote: 'La plataforma nos ayudó a cerrar procesos que antes se perdían entre correos y hojas de cálculo.',
    name: 'María Fernanda R.',
    role: 'Directora académica',
  },
  {
    quote: 'Ahora el equipo docente tiene más tiempo para enseñar y menos para administrar.',
    name: 'Carlos M.',
    role: 'Rector',
  },
  {
    quote: 'La experiencia de seguimiento y comunicación cambió por completo la relación con los padres.',
    name: 'Daniela T.',
    role: 'Coordinadora',
  },
];

const faqs = [
  {
    question: '¿La inteligencia artificial ya funciona o es un plan a futuro?',
    answer: 'Ya funciona. El motor de riesgo académico, el Copiloto de Rectoría, el Observador automático y el Tutor IA están activos y funcionando hoy, no en un roadmap.',
  },
  {
    question: '¿Playfesor funciona para instituciones pequeñas y grandes?',
    answer: 'Sí. La arquitectura está pensada para crecer desde un solo colegio hasta redes institucionales y secretarías de educación, sin rediseñar nada.',
  },
  {
    question: '¿Qué pasa con los datos de mis estudiantes?',
    answer: 'Los datos académicos se usan únicamente para operar la plataforma y generar analítica e IA para tu propia institución. No se comparten con terceros.',
  },
  {
    question: '¿Qué tan rápido se implementa frente a otras plataformas?',
    answer: 'La implementación es guiada, sin la curva de aprendizaje larga de sistemas más antiguos. El objetivo es que tu equipo trabaje con confianza desde la primera semana.',
  },
];

function ComparisonMark({ value }) {
  if (value === true) return <span className="mark mark-yes" aria-label="Sí">✓</span>;
  if (value === 'partial') return <span className="mark mark-partial" aria-label="Parcial">~</span>;
  return <span className="mark mark-no" aria-label="No">–</span>;
}

export default function LandingPage() {
  const heroRef = useRef(null);

  const handleHeroMove = useCallback((event) => {
    const node = heroRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    node.style.setProperty('--spot-x', `${x}%`);
    node.style.setProperty('--spot-y', `${y}%`);
  }, []);

  return (
    <div className="landing-page">
      <style>{styles}</style>

      <header className="topbar">
        <a className="brand" href="#inicio">
          <img className="brand-mark" src="/logo-icon.png" alt="Playfesor" />
          <span>Playfesor</span>
        </a>

        <nav className="nav-links" aria-label="Navegación principal">
          <a href="#caracteristicas">Características</a>
          <a href="#comparativa">Comparativa</a>
          <a href="#ia">IA</a>
          <a href="#planes">Planes</a>
          <a href="#contacto">Contacto</a>
        </nav>

        <Link className="nav-cta" to="/login">Ingresar</Link>
      </header>

      <main id="inicio">
        <section className="hero" ref={heroRef} onMouseMove={handleHeroMove}>
          <div className="hero-spotlight" aria-hidden="true" />
          <div className="hero-copy">
            <span className="eyebrow">Plataforma de inteligencia institucional educativa</span>
            <h1>La única plataforma que le dice a un rector colombiano qué va a pasar mañana en su colegio.</h1>
            <p>
              Playfesor ya funciona con colegios reales, con IA predictiva, copiloto ejecutivo y observador automático
              activos hoy mismo — no como promesa de roadmap, sino como algo que ningún competidor colombiano ofrece todavía.
            </p>

            <div className="hero-actions">
              <a className="btn btn-primary" href="#demo">Solicitar demo</a>
              <Link className="btn btn-secondary" to="/login">Ingresar al sistema</Link>
            </div>

            <div className="hero-badges">
              <span>✓ Ya funciona, con colegios reales</span>
              <span>✓ IA activa hoy, no en roadmap</span>
              <span>✓ Escala MEN Colombia</span>
            </div>
          </div>

          <div className="hero-visual" aria-hidden="true">
            <div className="mockup-card">
              <div className="mockup-topbar">
                <span />
                <span />
                <span />
              </div>
              <div className="mockup-body">
                <div className="mockup-sidebar" />
                <div className="mockup-content">
                  <div className="mockup-line large" />
                  <div className="mockup-line" />
                  <div className="mockup-grid">
                    <div className="mockup-box" />
                    <div className="mockup-box primary" />
                    <div className="mockup-box" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="stats-row" aria-label="Indicadores de impacto">
          {stats.map((stat, index) => (
            <Reveal as="div" delay={index * 80} className="stat-card" key={stat.label}>
              <Counter to={stat.value} suffix={stat.suffix} />
              <span>{stat.label}</span>
            </Reveal>
          ))}
        </section>

        <section className="trust-strip">
          <p>Confiado por instituciones que priorizan innovación, claridad y resultados.</p>
        </section>

        <section className="section section-light trust-section">
          <Reveal className="section-heading trust-heading">
            <span className="eyebrow">Confianza</span>
            <h2>Una solución preparada para instituciones que valoran innovación y orden.</h2>
            <p>Playfesor combina tecnología moderna, seguridad y experiencia de uso para acompañar la transformación educativa sin complicaciones.</p>
          </Reveal>
          <div className="logos-row" aria-label="Marcas de referencia">
            {trustLogos.map((logo, index) => (
              <Reveal as="div" delay={index * 60} className="logo-pill" key={logo}>{logo}</Reveal>
            ))}
          </div>
          <p className="illustrative-note">Nombres ilustrativos — próximamente compartiremos casos reales de instituciones aliadas.</p>
        </section>

        <section className="section section-light" id="caracteristicas">
          <Reveal className="section-heading">
            <span className="eyebrow">¿Por qué Playfesor?</span>
            <h2>Una base sólida hoy, con inteligencia real que ya está funcionando.</h2>
            <p>
              No es solo un sistema académico: es la única plataforma colombiana que ya convirtió datos en predicción,
              recomendaciones y automatización, en lugar de dejarlos dormidos en una base de datos.
            </p>
          </Reveal>

          <div className="pillars-grid">
            {pillars.map((pillar, index) => (
              <Reveal as="article" delay={index * 70} className="pillar-card" key={pillar.title}>
                <div className="feature-icon">{pillar.icon}</div>
                <h3>{pillar.title}</h3>
                <p>{pillar.text}</p>
              </Reveal>
            ))}
          </div>

          <div className="feature-grid">
            {features.map((feature, index) => (
              <Reveal as="article" delay={(index % 3) * 70} className="feature-card" key={feature.title}>
                <div className="feature-icon">{feature.icon}</div>
                {feature.badge && <span className="feature-badge">{feature.badge}</span>}
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="section" id="comparativa">
          <Reveal className="section-heading">
            <span className="eyebrow">Comparativa</span>
            <h2>Lo que otras plataformas colombianas todavía no tienen.</h2>
            <p>Q10 y Phidias muestran notas. Playfesor predice lo que va a pasar con esas notas, hoy, no en una versión futura.</p>
          </Reveal>

          <Reveal className="comparison-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th scope="col">Funcionalidad</th>
                  <th scope="col" className="col-playfesor">Playfesor</th>
                  <th scope="col">Phidias</th>
                  <th scope="col">Q10</th>
                  <th scope="col">Google Classroom</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.label}>
                    <th scope="row">{row.label}</th>
                    <td className="col-playfesor"><ComparisonMark value={row.playfesor} /></td>
                    <td><ComparisonMark value={row.phidias} /></td>
                    <td><ComparisonMark value={row.q10} /></td>
                    <td><ComparisonMark value={row.classroom} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
          <p className="illustrative-note">Comparativa elaborada a partir de análisis propio de funcionalidades públicas. Puede variar según el plan contratado de cada proveedor.</p>
        </section>

        <section className="section">
          <Reveal className="section-heading">
            <span className="eyebrow">Todo en un solo lugar</span>
            <h2>Una experiencia preparada para escritorio, tablet y celular.</h2>
            <p>Playfesor está diseñada para que docentes, directivos y familias trabajen con la misma claridad desde cualquier dispositivo.</p>
          </Reveal>

          <div className="devices-grid">
            <Reveal as="div" className="device-card">
              <div className="device-screen desktop" />
              <h3>Computador</h3>
              <p>Paneles amplios para decisiones estratégicas y gestión completa.</p>
            </Reveal>
            <Reveal as="div" delay={80} className="device-card">
              <div className="device-screen tablet" />
              <h3>Tablet</h3>
              <p>Consulta y seguimiento ágil en contexto académico y operativo.</p>
            </Reveal>
            <Reveal as="div" delay={160} className="device-card">
              <div className="device-screen mobile" />
              <h3>Celular</h3>
              <p>Acceso rápido para comunicación, alertas y tareas en movimiento.</p>
            </Reveal>
          </div>
        </section>

        <section className="section" id="ia">
          <Reveal className="section-heading">
            <span className="eyebrow">Inteligencia artificial</span>
            <h2>IA que potencia la educación, no que complique el trabajo.</h2>
            <p>
              Playfesor ya genera observaciones, planeaciones, informes, retroalimentación, alertas tempranas y
              recomendaciones — con una calidad pensada para el contexto educativo colombiano.
            </p>
          </Reveal>

          <div className="roles-grid">
            {roles.map((item, index) => (
              <Reveal as="article" delay={index * 70} className="role-card" key={item.role}>
                <span className="role-tag">{item.role}</span>
                <p className="role-problem">{item.problem}</p>
                <p className="role-solution">{item.solution}</p>
                <p className="role-impact">→ {item.impact}</p>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="section section-light" id="beneficios">
          <Reveal className="section-heading">
            <span className="eyebrow">Beneficios</span>
            <h2>Menos papeleo. Más impacto. Mayor claridad.</h2>
          </Reveal>

          <div className="benefits-grid">
            {[
              'Ahorro de tiempo operativo',
              'Automatización de procesos',
              'Información centralizada',
              'Mayor comunicación con las familias',
              'Detección temprana de riesgo académico',
              'Más productividad para todo el equipo',
            ].map((benefit, index) => (
              <Reveal as="div" delay={(index % 3) * 60} className="benefit-pill" key={benefit}>{benefit}</Reveal>
            ))}
          </div>
        </section>

        <section className="section" id="planes">
          <Reveal className="section-heading">
            <span className="eyebrow">Planes</span>
            <h2>Un plan para cada tamaño de institución.</h2>
            <p>Cada plan incluye acompañamiento en la implementación. Agenda una demo para conocer el plan y la propuesta ajustada a tu colegio.</p>
          </Reveal>

          <div className="plans-grid">
            {plans.map((plan, index) => (
              <Reveal as="article" delay={index * 90} className={`plan-card ${plan.highlight ? 'plan-highlight' : ''}`} key={plan.name}>
                {plan.highlight && <span className="plan-badge">Más elegido</span>}
                <span className="plan-tag">{plan.tag}</span>
                <h3>{plan.name}</h3>
                <p className="plan-audience">{plan.audience}</p>
                <ul className="plan-features">
                  {plan.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
                <a className={`btn ${plan.highlight ? 'btn-primary' : 'btn-secondary'} plan-cta`} href="#demo">Agenda una demo</a>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="section">
          <Reveal className="section-heading">
            <span className="eyebrow">Cómo funciona</span>
            <h2>Un proceso simple para una transformación real.</h2>
          </Reveal>

          <div className="steps-grid">
            {steps.map((step, index) => (
              <Reveal as="div" delay={index * 80} className="step-card" key={step}>
                <div className="step-number">0{index + 1}</div>
                <p>{step}</p>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="section section-light" id="demo">
          <Reveal className="section-heading">
            <span className="eyebrow">Testimonios</span>
            <h2>Ejemplos de lo que una institución puede lograr.</h2>
            <p>Estos testimonios son demostrativos y pueden reemplazarse por historias reales cuando estén disponibles.</p>
          </Reveal>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <Reveal as="article" delay={index * 80} className="testimonial-card" key={testimonial.name}>
                <p>“{testimonial.quote}”</p>
                <div>
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.role}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="section">
          <Reveal className="section-heading">
            <span className="eyebrow">Preguntas frecuentes</span>
            <h2>Todo lo que necesitas saber antes de dar el siguiente paso.</h2>
          </Reveal>

          <div className="faq-list">
            {faqs.map((faq, index) => (
              <Reveal as="details" delay={index * 60} className="faq-item" key={faq.question}>
                <summary>{faq.question}</summary>
                <p>{faq.answer}</p>
              </Reveal>
            ))}
          </div>
        </section>
      </main>

      <section className="cta-section">
        <Reveal className="cta-card">
          <span className="eyebrow">Convierte tu operación escolar</span>
          <h2>Haz que tu institución avance con la única plataforma colombiana con IA educativa activa hoy.</h2>
          <p>Agenda una demo y descubre cómo Playfesor puede ayudar a tu equipo a trabajar mejor, con menos esfuerzo y más impacto.</p>
          <div className="hero-actions">
            <a className="btn btn-primary" href="mailto:hola@playfesor.co">Solicitar demo</a>
            <Link className="btn btn-secondary" to="/login">Ingresar al sistema</Link>
          </div>
        </Reveal>
      </section>

      <footer className="footer" id="contacto">
        <div>
          <h3>Playfesor</h3>
          <p>Software educativo para colegios que buscan crecer con tecnología, orden y visión.</p>
        </div>
        <div className="footer-links">
          <a href="mailto:hola@playfesor.co">hola@playfesor.co</a>
          <a href="https://wa.me/573001234567" target="_blank" rel="noreferrer">WhatsApp</a>
          <a href="/login">Ingresar</a>
          <a href="#inicio">Inicio</a>
        </div>
      </footer>
    </div>
  );
}

const styles = `
  :root {
    color-scheme: dark;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    margin: 0;
    background: #07111f;
    color: #f5f7ff;
  }

  * {
    box-sizing: border-box;
  }

  .landing-page {
    min-height: 100vh;
    background:
      radial-gradient(circle at top left, rgba(132, 92, 246, 0.24), transparent 30%),
      radial-gradient(circle at 85% 15%, rgba(58, 167, 255, 0.16), transparent 24%),
      linear-gradient(135deg, #07111f 0%, #0d1628 45%, #111c32 100%);
    color: #f5f7ff;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    overflow-x: hidden;
  }

  .topbar {
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 24px;
    backdrop-filter: blur(16px);
    background: rgba(7, 17, 31, 0.72);
    border-bottom: 1px solid rgba(255,255,255,0.08);
  }

  .brand {
    display: flex;
    align-items: center;
    gap: 12px;
    color: white;
    font-weight: 700;
    text-decoration: none;
    font-size: 1.05rem;
  }

  .brand-mark {
    width: 32px;
    height: 32px;
    object-fit: contain;
    filter: drop-shadow(0 6px 14px rgba(80, 134, 255, 0.35));
  }

  .nav-links {
    display: flex;
    gap: 20px;
  }

  .nav-links a,
  .footer-links a {
    color: #d8def5;
    text-decoration: none;
    transition: color 0.2s ease;
  }

  .nav-links a:hover,
  .footer-links a:hover {
    color: white;
  }

  .nav-cta,
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    padding: 12px 18px;
    text-decoration: none;
    font-weight: 600;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .nav-cta {
    background: rgba(255,255,255,0.09);
    color: white;
  }

  .nav-cta:hover,
  .btn:hover {
    transform: translateY(-2px);
  }

  .hero,
  .section,
  .footer,
  .cta-section {
    width: min(1180px, calc(100% - 32px));
    margin: 0 auto;
  }

  .hero {
    display: grid;
    grid-template-columns: 1.1fr 0.9fr;
    gap: 32px;
    padding: 72px 0 48px;
    align-items: center;
    position: relative;
  }

  .hero-spotlight {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(480px circle at var(--spot-x, 30%) var(--spot-y, 20%), rgba(124, 92, 255, 0.16), transparent 60%);
    transition: background 0.1s ease-out;
    z-index: 0;
  }

  .hero-copy,
  .hero-visual {
    position: relative;
    z-index: 1;
  }

  .hero::before,
  .hero::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    filter: blur(40px);
    opacity: 0.35;
    pointer-events: none;
  }

  .hero::before {
    width: 280px;
    height: 280px;
    background: rgba(124, 92, 255, 0.34);
    top: -30px;
    right: -140px;
    animation: float 8s ease-in-out infinite;
  }

  .hero::after {
    width: 240px;
    height: 240px;
    background: rgba(58, 167, 255, 0.24);
    bottom: -80px;
    left: -100px;
    animation: float 10s ease-in-out infinite reverse;
  }

  .eyebrow {
    display: inline-block;
    margin-bottom: 12px;
    font-size: 0.8rem;
    letter-spacing: 0.24em;
    text-transform: uppercase;
    color: #8fd2ff;
    font-weight: 700;
  }

  .hero h1,
  .section-heading h2 {
    font-size: clamp(2rem, 3.4vw, 3.3rem);
    line-height: 1.08;
    margin-bottom: 16px;
    letter-spacing: -0.03em;
  }

  .hero p,
  .section-heading p,
  .feature-card p,
  .role-card p,
  .testimonial-card p,
  .faq-item p,
  .footer p {
    color: #c9d0e1;
    line-height: 1.7;
    font-size: 1rem;
  }

  .hero-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    margin: 24px 0 20px;
  }

  .btn-primary {
    background: linear-gradient(135deg, #7c5cff, #3aa7ff);
    color: white;
    box-shadow: 0 16px 35px rgba(80, 134, 255, 0.25);
  }

  .btn-secondary {
    background: rgba(255,255,255,0.08);
    color: white;
    border: 1px solid rgba(255,255,255,0.12);
  }

  .hero-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .hero-badges span,
  .benefit-pill {
    padding: 10px 14px;
    border-radius: 999px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.1);
    color: #f4f6ff;
    font-size: 0.95rem;
  }

  .hero-visual {
    display: flex;
    justify-content: center;
    animation: rise 0.8s ease-out;
  }

  .mockup-card {
    width: min(470px, 100%);
    padding: 18px;
    border-radius: 28px;
    background: linear-gradient(145deg, rgba(255,255,255,0.16), rgba(255,255,255,0.05));
    border: 1px solid rgba(255,255,255,0.12);
    box-shadow: 0 30px 70px rgba(2, 7, 23, 0.4);
  }

  .mockup-topbar {
    display: flex;
    gap: 8px;
    margin-bottom: 14px;
  }

  .mockup-topbar span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: rgba(255,255,255,0.22);
  }

  .mockup-body {
    display: grid;
    grid-template-columns: 74px 1fr;
    gap: 14px;
  }

  .mockup-sidebar {
    border-radius: 16px;
    background: rgba(255,255,255,0.08);
    min-height: 220px;
  }

  .mockup-content {
    border-radius: 16px;
    background: rgba(7, 17, 31, 0.65);
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .mockup-line {
    height: 10px;
    border-radius: 999px;
    background: rgba(255,255,255,0.14);
  }

  .mockup-line.large {
    width: 60%;
  }

  .mockup-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-top: 10px;
  }

  .mockup-box {
    min-height: 70px;
    border-radius: 14px;
    background: rgba(255,255,255,0.1);
  }

  .mockup-box.primary {
    background: linear-gradient(135deg, #7c5cff, #3aa7ff);
  }

  .stats-row {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    width: min(1180px, calc(100% - 32px));
    margin: 0 auto 18px;
  }

  .stat-card {
    padding: 20px;
    border-radius: 20px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.08);
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .stat-card strong {
    font-size: 1.5rem;
    color: white;
  }

  .stat-card span {
    color: #9fb0cd;
    font-size: 0.95rem;
  }

  .trust-section {
    text-align: center;
  }

  .trust-heading {
    margin: 0 auto 24px;
  }

  .logos-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
  }

  .logo-pill {
    padding: 12px 16px;
    border-radius: 999px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.08);
    color: #f4f6ff;
  }

  .illustrative-note {
    text-align: center;
    color: #7c8aab;
    font-size: 0.82rem;
    margin-top: 16px;
  }

  .trust-strip {
    text-align: center;
    padding: 14px 20px 40px;
    color: #9fb0cd;
  }

  .section {
    padding: 56px 0;
  }

  .section-light {
    padding: 64px 24px;
    border-radius: 28px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
  }

  .section-heading {
    max-width: 740px;
    margin-bottom: 28px;
  }

  .section-heading h2 {
    font-size: clamp(1.7rem, 2.5vw, 2.4rem);
    margin-bottom: 10px;
  }

  .feature-grid,
  .pillars-grid,
  .benefits-grid,
  .steps-grid,
  .testimonials-grid,
  .roles-grid,
  .plans-grid {
    display: grid;
    gap: 18px;
  }

  .feature-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin-top: 18px;
  }

  .pillars-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .feature-card,
  .pillar-card,
  .role-card,
  .step-card,
  .testimonial-card,
  .faq-item,
  .device-card,
  .plan-card {
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 22px;
    padding: 20px;
    box-shadow: 0 12px 30px rgba(2, 7, 23, 0.16);
    position: relative;
  }

  .feature-card:hover,
  .pillar-card:hover,
  .device-card:hover,
  .role-card:hover,
  .plan-card:hover {
    transform: translateY(-4px);
    border-color: rgba(143, 210, 255, 0.32);
    box-shadow: 0 18px 40px rgba(2, 7, 23, 0.24);
  }

  .feature-card,
  .pillar-card,
  .device-card,
  .role-card,
  .plan-card {
    transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
  }

  .feature-badge {
    position: absolute;
    top: 18px;
    right: 18px;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    padding: 4px 9px;
    border-radius: 999px;
    color: #0b1120;
    background: linear-gradient(135deg, #8fd2ff, #7c5cff);
  }

  .feature-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    margin-bottom: 12px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(124, 92, 255, 0.28), rgba(58, 167, 255, 0.24));
    color: #8fd2ff;
    font-size: 1.1rem;
  }

  .feature-card h3,
  .role-card h3,
  .step-card .step-number {
    margin-bottom: 8px;
  }

  .devices-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;
  }

  .device-card {
    text-align: left;
  }

  .device-screen {
    height: 140px;
    border-radius: 18px;
    margin-bottom: 14px;
    background: linear-gradient(135deg, rgba(124, 92, 255, 0.24), rgba(58, 167, 255, 0.18));
    border: 1px solid rgba(255,255,255,0.1);
    position: relative;
    overflow: hidden;
  }

  .device-screen::before {
    content: '';
    position: absolute;
    inset: 18px;
    border-radius: 12px;
    background: rgba(255,255,255,0.08);
  }

  .device-screen.desktop::after {
    content: '';
    position: absolute;
    width: 76%;
    height: 24px;
    left: 12%;
    bottom: 18px;
    border-radius: 999px;
    background: rgba(255,255,255,0.16);
  }

  .device-screen.tablet {
    transform: rotate(-4deg);
  }

  .device-screen.mobile {
    transform: rotate(4deg);
  }

  /* Comparativa */
  .comparison-wrapper {
    overflow-x: auto;
    border-radius: 22px;
    border: 1px solid rgba(255,255,255,0.08);
    background: rgba(255,255,255,0.04);
  }

  .comparison-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 620px;
  }

  .comparison-table th,
  .comparison-table td {
    padding: 14px 16px;
    text-align: center;
    border-bottom: 1px solid rgba(255,255,255,0.07);
    font-size: 0.92rem;
    color: #d8def5;
  }

  .comparison-table thead th {
    color: #9fb0cd;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.72rem;
    letter-spacing: 0.06em;
  }

  .comparison-table th[scope="row"] {
    text-align: left;
    font-weight: 500;
    color: #f4f6ff;
  }

  .comparison-table tbody tr:last-child th,
  .comparison-table tbody tr:last-child td {
    border-bottom: none;
  }

  .col-playfesor {
    background: linear-gradient(180deg, rgba(124, 92, 255, 0.14), rgba(58, 167, 255, 0.08));
    box-shadow: inset 0 0 0 1px rgba(143, 210, 255, 0.28);
  }

  .mark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    font-weight: 700;
    font-size: 0.85rem;
  }

  .mark-yes {
    background: rgba(122, 230, 170, 0.16);
    color: #7ae6aa;
  }

  .mark-partial {
    background: rgba(255, 205, 112, 0.16);
    color: #ffcd70;
  }

  .mark-no {
    background: rgba(255, 255, 255, 0.06);
    color: #5c6884;
  }

  /* IA por rol */
  .roles-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .role-card {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .role-tag {
    display: inline-block;
    align-self: flex-start;
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #8fd2ff;
    background: rgba(143, 210, 255, 0.12);
    margin-bottom: 6px;
  }

  .role-problem {
    color: #9fb0cd;
    font-size: 0.9rem;
  }

  .role-solution {
    color: #f4f6ff;
    font-weight: 600;
    font-size: 0.95rem;
  }

  .role-impact {
    color: #8fd2ff;
    font-size: 0.88rem;
    margin-top: auto;
  }

  .benefits-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  /* Planes */
  .plans-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .plan-card {
    display: flex;
    flex-direction: column;
  }

  .plan-highlight {
    border-color: rgba(143, 210, 255, 0.4);
    background: linear-gradient(180deg, rgba(124, 92, 255, 0.14), rgba(58, 167, 255, 0.06));
  }

  .plan-badge {
    position: absolute;
    top: -12px;
    right: 20px;
    padding: 6px 14px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    color: #0b1120;
    background: linear-gradient(135deg, #8fd2ff, #7c5cff);
  }

  .plan-tag {
    color: #8fd2ff;
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .plan-card h3 {
    margin: 6px 0 4px;
    font-size: 1.3rem;
  }

  .plan-audience {
    color: #9fb0cd;
    font-size: 0.88rem;
    margin-bottom: 14px;
  }

  .plan-features {
    list-style: none;
    padding: 0;
    margin: 0 0 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex-grow: 1;
  }

  .plan-features li {
    color: #d8def5;
    font-size: 0.9rem;
    padding-left: 22px;
    position: relative;
  }

  .plan-features li::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #7ae6aa;
    font-weight: 700;
  }

  .plan-cta {
    width: 100%;
  }

  .steps-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .step-card {
    text-align: center;
  }

  .step-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: rgba(255,255,255,0.08);
    font-weight: 700;
    color: #8fd2ff;
    margin-bottom: 12px;
  }

  .testimonials-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .testimonial-card {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 220px;
  }

  .testimonial-card strong {
    display: block;
    margin-top: 16px;
  }

  .testimonial-card span {
    color: #8fd2ff;
    font-size: 0.9rem;
  }

  .faq-list {
    display: grid;
    gap: 12px;
  }

  .faq-item summary {
    cursor: pointer;
    font-weight: 600;
    list-style: none;
  }

  .faq-item summary::-webkit-details-marker {
    display: none;
  }

  .faq-item p {
    margin-top: 10px;
  }

  .cta-section {
    margin-top: 20px;
    padding-bottom: 24px;
  }

  .cta-card {
    padding: 34px;
    border-radius: 30px;
    background: linear-gradient(135deg, rgba(124, 92, 255, 0.2), rgba(58, 167, 255, 0.14));
    border: 1px solid rgba(255,255,255,0.12);
    box-shadow: 0 24px 60px rgba(2, 7, 23, 0.24);
    text-align: center;
  }

  .cta-card h2 {
    font-size: clamp(1.5rem, 2.2vw, 2.2rem);
    margin: 8px 0 12px;
  }

  .footer {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    padding: 40px 0 56px;
    border-top: 1px solid rgba(255,255,255,0.08);
    margin-top: 20px;
  }

  .footer-links {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  /* Revelado por scroll */
  .reveal {
    opacity: 0;
    transform: translateY(24px);
    transition: opacity 0.6s ease, transform 0.6s ease;
  }

  .reveal.is-visible {
    opacity: 1;
    transform: translateY(0);
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-16px); }
  }

  @keyframes rise {
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 900px) {
    .hero,
    .feature-grid,
    .pillars-grid,
    .benefits-grid,
    .steps-grid,
    .testimonials-grid,
    .roles-grid,
    .plans-grid,
    .devices-grid,
    .stats-row {
      grid-template-columns: 1fr;
    }

    .hero {
      padding-top: 48px;
    }

    .stats-row {
      grid-template-columns: 1fr;
    }

    .nav-links {
      display: none;
    }

    .footer {
      flex-direction: column;
    }
  }

  @media (min-width: 901px) and (max-width: 1100px) {
    .feature-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .roles-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 640px) {
    .topbar {
      padding: 14px 16px;
    }

    .hero,
    .section,
    .footer,
    .cta-section {
      width: min(100% - 20px, 1180px);
    }

    .hero-actions {
      flex-direction: column;
    }

    .section-light {
      padding: 44px 16px;
    }

    .plan-badge {
      right: 12px;
    }
  }
`;
